/**
 * API + UI partajat: completări propuse + ciclul de viață (pauză/redeschidere)
 * pentru workflow (mentenanță programată / Lucrările mele) și maintenance_log (intervenții urgente).
 */
import { state, toast, escHtml, formatDateTimeRO, customPrompt } from './core';
import type { UrgentaActiva } from './types';

export type WorkEntityType = 'workflow' | 'maintenance_log';

export const WORK_LIFECYCLE_LABELS = {
    pause: 'Pauză (continuă mâine)',
    resumeWorkflow: 'Redeschide lucrarea',
    resumeUrgent: 'Redeschide intervenția',
    completeWorkflow: 'Finalizează revizia',
    completeUrgent: 'Finalizează intervenția',
};

const ENRICHMENT_STATUS: Record<string, { label: string; icon: string; color: string }> = {
    pending: { label: 'Așteaptă validare', icon: '⏳', color: '#f59e0b' },
    approved: { label: 'Aprobat', icon: '✅', color: '#22c55e' },
    rejected: { label: 'Respins', icon: '❌', color: '#ef4444' },
};

export function workApiBase(entityType: WorkEntityType, entityId: number): string {
    return `/api/work/${entityType}/${entityId}`;
}

export async function workFetchEnrichments(entityType: WorkEntityType, entityId: number): Promise<any[]> {
    const r = await fetch(workApiBase(entityType, entityId) + '/enrichments');
    if (!r.ok) throw new Error('HTTP ' + r.status);
    return r.json();
}

export async function workPause(entityType: WorkEntityType, entityId: number, payload: Record<string, unknown> = {}): Promise<void> {
    const r = await fetch(workApiBase(entityType, entityId) + '/lifecycle/pause', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(payload),
    });
    const body = await r.json().catch(() => ({}));
    if (!r.ok) throw new Error(body.error || ('HTTP ' + r.status));
}

export async function workResume(entityType: WorkEntityType, entityId: number): Promise<void> {
    const r = await fetch(workApiBase(entityType, entityId) + '/lifecycle/resume', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: '{}',
    });
    const body = await r.json().catch(() => ({}));
    if (!r.ok) throw new Error(body.error || ('HTTP ' + r.status));
}

export async function workSubmitEnrichment(
    entityType: WorkEntityType,
    entityId: number,
    opts: { text?: string; contentType?: string; stepOrder?: number; file?: File | null },
): Promise<void> {
    if (opts.file) {
        const fd = new FormData();
        fd.append('file', opts.file);
        if (opts.text) fd.append('text', opts.text);
        fd.append('step_order', String(opts.stepOrder || 0));
        const r = await fetch(workApiBase(entityType, entityId) + '/enrichments', { method: 'POST', body: fd });
        const body = await r.json().catch(() => ({}));
        if (!r.ok) throw new Error(body.error || ('HTTP ' + r.status));
        return;
    }
    const r = await fetch(workApiBase(entityType, entityId) + '/enrichments', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
            text: opts.text,
            content_type: opts.contentType || 'note',
            step_order: opts.stepOrder || 0,
        }),
    });
    const body = await r.json().catch(() => ({}));
    if (!r.ok) throw new Error(body.error || ('HTTP ' + r.status));
}

export async function workReviewEnrichment(enrichmentId: number, decision: 'approved' | 'rejected', reviewNotes: string): Promise<void> {
    const r = await fetch('/api/work/enrichments/' + enrichmentId + '/review', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ status: decision, review_notes: reviewNotes || null }),
    });
    const body = await r.json().catch(() => ({}));
    if (!r.ok) throw new Error(body.error || ('HTTP ' + r.status));
}

export async function workFetchActiveUrgentLogs(): Promise<UrgentaActiva[]> {
    const r = await fetch('/api/work/maintenance_log/active');
    if (!r.ok) return [];
    return r.json();
}

/** HTML fragment for enrichment form + list container */
export function workEnrichmentsPanelHtml(prefix: string): string {
    return `<div id="${prefix}-enrichments-panel" class="wf-enrichments-panel">
        <div class="wf-enrichments-header"><span>📝 Completări propuse</span><span id="${prefix}-enrichments-pending" class="wf-journal-badge" style="display:none">0</span></div>
        <p class="wf-hint wf-enrichments-hint">Pași, explicații sau media suplimentare — necesită validare inginer înainte de înregistrare oficială.</p>
        <div class="wf-enrichments-form">
            <div class="wf-enrichments-form-row">
                <select id="${prefix}-enr-type" class="wf-enr-select"><option value="note">Notă / explicație</option><option value="step">Pas suplimentar</option></select>
                <input type="number" id="${prefix}-enr-order" placeholder="Ordine" min="0" class="wf-enr-order" title="Ordine pas">
            </div>
            <textarea id="${prefix}-enr-text" rows="2" placeholder="Descrie pasul sau explicația suplimentară..." class="wf-enr-textarea"></textarea>
            <div class="wf-enrichments-media-row">
                <label class="btn btn-sm btn-outline wf-enr-file-label">📷 Foto / 🎬 Video
                    <input type="file" id="${prefix}-enr-file" accept="image/jpeg,image/png,image/webp,video/mp4,video/webm" style="display:none">
                </label>
                <span id="${prefix}-enr-file-name" class="wf-enr-file-name"></span>
            </div>
            <button type="button" class="btn btn-sm wf-enr-submit" id="${prefix}-enr-submit">+ Adaugă completare</button>
        </div>
        <div id="${prefix}-enrichments-list" class="wf-enrichments-list"></div>
    </div>`;
}

export async function workRefreshEnrichmentsPanel(
    entityType: WorkEntityType,
    entityId: number,
    prefix: string,
): Promise<void> {
    const listEl = document.getElementById(prefix + '-enrichments-list');
    const pendingEl = document.getElementById(prefix + '-enrichments-pending');
    if (!listEl || !entityId) return;
    const isReviewer = state.currentUser && (state.currentUser.role === 'engineer' || state.currentUser.role === 'master');
    try {
        const items = await workFetchEnrichments(entityType, entityId);
        const pendingCount = items.filter(i => i.status === 'pending').length;
        if (pendingEl) {
            pendingEl.textContent = String(pendingCount);
            pendingEl.style.display = pendingCount > 0 ? 'inline-block' : 'none';
        }
        if (!items.length) {
            listEl.innerHTML = '<p class="wf-hint" style="padding:8px 14px">Nicio completare propusă încă.</p>';
            return;
        }
        let html = '';
        items.forEach(item => {
            const st = ENRICHMENT_STATUS[item.status] || ENRICHMENT_STATUS.pending;
            const typeLabel = item.content_type === 'step' ? 'Pas suplimentar'
                : item.content_type === 'photo' ? 'Foto'
                : item.content_type === 'video' ? 'Video' : 'Notă';
            html += '<div class="wf-enrichment-item">';
            html += '<div class="wf-enrichment-item-header">';
            html += '<span class="wf-enrichment-type">' + escHtml(typeLabel) + '</span>';
            html += '<span class="wf-enrichment-status" style="color:' + st.color + '">' + st.icon + ' ' + st.label + '</span>';
            html += '</div>';
            if (item.text_content) html += '<div class="wf-enrichment-text">' + escHtml(item.text_content) + '</div>';
            if (item.file_url && (item.content_type === 'photo' || item.content_type === 'video')) {
                if (item.content_type === 'video') {
                    html += '<video src="' + escHtml(item.file_url) + '" controls class="wf-enrichment-media"></video>';
                } else {
                    html += '<img src="' + escHtml(item.file_url) + '" class="wf-enrichment-media" alt="foto" onclick="window.open(\'' + escHtml(item.file_url) + '\',\'_blank\')">';
                }
            }
            html += '<div class="wf-enrichment-meta">' + escHtml(item.created_by || '') + ' · ' + formatDateTimeRO(item.created_at || '') + '</div>';
            if (item.review_notes && item.status === 'rejected') {
                html += '<div class="wf-enrichment-review-note">Motiv respingere: ' + escHtml(item.review_notes) + '</div>';
            }
            if (isReviewer && item.status === 'pending') {
                html += '<div class="wf-enrichment-actions">';
                html += '<button type="button" class="btn btn-sm ws-enr-approve" data-eid="' + item.id + '" style="background:var(--green);color:#fff">Validează</button>';
                html += '<button type="button" class="btn btn-sm ws-enr-reject" data-eid="' + item.id + '" style="background:var(--red);color:#fff;margin-left:6px">Respinge</button>';
                html += '</div>';
            }
            html += '</div>';
        });
        listEl.innerHTML = html;
        listEl.querySelectorAll('.ws-enr-approve').forEach(btn => {
            btn.addEventListener('click', async () => {
                const id = parseInt((btn as HTMLElement).dataset.eid || '0', 10);
                const notes = (await customPrompt('Comentariu validare (opțional):', '')) || '';
                try {
                    await workReviewEnrichment(id, 'approved', notes.trim());
                    toast('Completare aprobată', 'success');
                    await workRefreshEnrichmentsPanel(entityType, entityId, prefix);
                } catch (e: unknown) {
                    toast('Eroare: ' + ((e as Error).message || String(e)), 'error');
                }
            });
        });
        listEl.querySelectorAll('.ws-enr-reject').forEach(btn => {
            btn.addEventListener('click', async () => {
                const id = parseInt((btn as HTMLElement).dataset.eid || '0', 10);
                const notes = (await customPrompt('Motiv respingere (obligatoriu):', '')) || '';
                if (!notes.trim()) { toast('Motiv obligatoriu', 'warning'); return; }
                try {
                    await workReviewEnrichment(id, 'rejected', notes.trim());
                    toast('Completare respinsă', 'info');
                    await workRefreshEnrichmentsPanel(entityType, entityId, prefix);
                } catch (e: unknown) {
                    toast('Eroare: ' + ((e as Error).message || String(e)), 'error');
                }
            });
        });
    } catch (_e) {
        listEl.innerHTML = '<p class="wf-hint" style="padding:8px 14px">Eroare la încărcarea completărilor.</p>';
    }
}

export function workWireEnrichmentForm(
    entityType: WorkEntityType,
    getEntityId: () => number | null,
    prefix: string,
): void {
    const fileInput = document.getElementById(prefix + '-enr-file') as HTMLInputElement | null;
    const fileNameEl = document.getElementById(prefix + '-enr-file-name');
    if (fileInput && fileNameEl) {
        fileInput.addEventListener('change', () => {
            fileNameEl.textContent = fileInput.files && fileInput.files[0] ? fileInput.files[0].name : '';
        });
    }
    const submitBtn = document.getElementById(prefix + '-enr-submit');
    submitBtn?.addEventListener('click', async () => {
        const entityId = getEntityId();
        if (!entityId) { toast('Nu există lucrare activă', 'error'); return; }
        const textEl = document.getElementById(prefix + '-enr-text') as HTMLTextAreaElement | null;
        const typeEl = document.getElementById(prefix + '-enr-type') as HTMLSelectElement | null;
        const orderEl = document.getElementById(prefix + '-enr-order') as HTMLInputElement | null;
        const fileEl = document.getElementById(prefix + '-enr-file') as HTMLInputElement | null;
        const text = (textEl?.value || '').trim();
        const file = fileEl?.files && fileEl.files[0] ? fileEl.files[0] : null;
        if (!text && !file) {
            toast('Adaugă text sau selectează un fișier foto/video', 'warning');
            return;
        }
        try {
            await workSubmitEnrichment(entityType, entityId, {
                text: text || undefined,
                contentType: typeEl?.value || 'note',
                stepOrder: parseInt(orderEl?.value || '0', 10) || 0,
                file,
            });
            if (textEl) textEl.value = '';
            if (fileEl) fileEl.value = '';
            if (fileNameEl) fileNameEl.textContent = '';
            const isAuto = state.currentUser && (state.currentUser.role === 'engineer' || state.currentUser.role === 'master');
            toast(isAuto ? 'Completare adăugată și aprobată' : 'Completare trimisă spre validare', 'success');
            await workRefreshEnrichmentsPanel(entityType, entityId, prefix);
        } catch (e: unknown) {
            toast('Eroare: ' + ((e as Error).message || String(e)), 'error');
        }
    });
}
